import React, { createContext, useContext, useState, useEffect } from "react";
import { getCurrentUser } from "../api/endpoints/user";
import { logout as apiLogout } from "../api/endpoints/auth";

// Create the authentication context
const AuthContext = createContext();

// Hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated
  const isAuthenticated = localStorage.getItem("token");
  const googleToken = localStorage.getItem("googleToken");

  // Fetch user details on mount if authenticated
  useEffect(() => {
    const fetchUserDetails = async () => {
      console.log("[Auth] Starting authentication check");
      if (!isAuthenticated && !googleToken) {
        setLoading(false);
        return;
      }

      try {
        const config = {
          headers: {
            Authorization: isAuthenticated ? `Bearer ${isAuthenticated}` : undefined,
            "Google-Token": googleToken ? `Bearer ${googleToken}` : undefined,
          },
        };

        console.log("[Auth] Fetching user details");
        const response = await getCurrentUser(config);
        setUser(response.data);
        setLoading(false);
      } catch (error) {
        console.error("[Auth] Error fetching user details:", error);
        setError(error);
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [isAuthenticated, googleToken]);

  // Logout function
  const logout = async () => {
    try {
      await apiLogout();
      localStorage.removeItem("token");
      localStorage.removeItem("googleToken");
      setUser(null);
      // Attempt to disable Google auto select / clear Google session hints
      if (window.google && window.google.accounts && window.google.accounts.id) {
        google.accounts.id.disableAutoSelect();
        console.log("[Auth] Google auto select disabled during logout.");
      }
      window.location.href = "/login";
    } catch (error) {
      console.error("[Auth] Logout error:", error);
      setError(error);
    }
  };

  // Login function that sets tokens and user data
  const login = (token, googleToken = null, userData = null) => {
    if (token) {
      localStorage.setItem("token", token);
    }
    if (googleToken) {
      localStorage.setItem("googleToken", googleToken);
    }
    if (userData) {
      setUser(userData);
    }
  };

  // Provide auth context values
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!isAuthenticated || !!googleToken,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext; 